// src/pages/management/Management.jsx
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  StatCard,
  MonthlyClinicSelection,
  Page,
  Tabs,
  DateStatCard,
  useReportDate,
  usePersistedState,
  ForecastingSummaryBar,
} from "@common";
import { Overview, Forecasting, ProfileDirectoryTabPanel } from "@pages";
import { useOverviewClinicSummary, useForecastsBatch } from "@api/hooks";
import { useAuth } from "@api/useAuth";
import { overviewSummaryStats, isClinicScopedUser, torontoDateParts } from "@utils";

export default function Management() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const role = user?.role;

  // Strip stale query params, but keep ?tab=... and the forecast month param.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const allowed = new Set(["tab", "forecast_month"]);
    const hasOther = Array.from(params.keys()).some((k) => !allowed.has(k));
    if (hasOther) {
      const kept = new URLSearchParams();
      for (const k of allowed) {
        const v = params.get(k);
        if (v) kept.set(k, v);
      }
      const search = kept.toString();
      navigate(
        { pathname: window.location.pathname, search: search ? `?${search}` : "" },
        { replace: true },
      );
    }
  }, [navigate]);

  const { reportDate, setReportDate } = useReportDate();

  const [tab, setTab] = useState(() => searchParams.get("tab") || "overview");

  // Persist tab in URL so refresh stays on the same tab
  useEffect(() => {
    const current = new URLSearchParams(window.location.search).get("tab");
    if (tab && tab !== current) {
      const next = new URLSearchParams(window.location.search);
      next.set("tab", tab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ── Forecasting tab state ────────────────────────────────────────────────
  // CHANGED: was useState([]) — now persisted so selected clinics survive a page refresh.
  // Key is _v2 to bust any stale value stored by the previous null-based auto-select approach.
  const [forecastingClinicFilters, setForecastingClinicFilters] = usePersistedState(
    "management:forecastingClinicFilters_v2",
    [],
  );
  const [clinicSummaries, setClinicSummaries] = useState({});

  const [forecastCalYear, setForecastCalYear] = useState(() => {
    const fromUrl = searchParams.get("forecast_month");
    if (fromUrl && /^\d{4}-\d{2}$/.test(fromUrl)) return Number(fromUrl.split("-")[0]);
    return torontoDateParts().year;
  });
  const [forecastCalMonth, setForecastCalMonth] = useState(() => {
    const fromUrl = searchParams.get("forecast_month");
    if (fromUrl && /^\d{4}-\d{2}$/.test(fromUrl)) return Number(fromUrl.split("-")[1]) - 1;
    return torontoDateParts().month - 1;
  });
  const forecastMonthValue = `${forecastCalYear}-${String(forecastCalMonth + 1).padStart(2, "0")}`;

  // Persist forecast month in URL so a page refresh stays on the same month
  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    if (next.get("forecast_month") !== forecastMonthValue) {
      next.set("forecast_month", forecastMonthValue);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastMonthValue]);

  const now = torontoDateParts();
  const forecastIsPastMonth =
    forecastCalYear < now.year ||
    (forecastCalYear === now.year && forecastCalMonth < now.month - 1);

  const forecastBatchMM = String(forecastCalMonth + 1).padStart(2, "0");
  const forecastBatchDateFrom = `${forecastCalYear}-${forecastBatchMM}-01`;
  const forecastBatchDateTo = `${forecastCalYear}-${forecastBatchMM}-${String(
    new Date(forecastCalYear, forecastCalMonth + 1, 0).getDate(),
  ).padStart(2, "0")}`;

  function handleForecastCalNav(year, month) {
    setForecastCalYear(year);
    setForecastCalMonth(month);
  }

  function handleForecastMonthChange(monthStr) {
    if (!monthStr) return;
    const [yr, mo] = monthStr.split("-").map(Number);
    setForecastCalYear(yr);
    setForecastCalMonth(mo - 1);
  }
  // ────────────────────────────────────────────────────────────────────────

  const {
    data: clinicSummaryData,
    isLoading: isLoadingSummary,
    refetch: refetchClinicSummary,
  } = useOverviewClinicSummary({ date: reportDate }, { enablePolling: true });

  // The summary query lives here, above the tab switch, so it never remounts when
  // the user comes back to Overview — without this an explicit refetch, the table
  // would show whatever was cached when the page first loaded.
  useEffect(() => {
    if (tab === "overview") refetchClinicSummary();
  }, [tab, refetchClinicSummary]);

  const clinics = useMemo(() => {
    const results = clinicSummaryData?.results || [];
    // For clinic-scoped roles the backend already filters, but during role-test mode
    // the real user is admin so the API returns everything — filter client-side.
    if (isClinicScopedUser(user) && user?.clinic?.length) {
      const allowed = new Set(user.clinic.map((c) => c.toUpperCase()));
      return results.filter((c) => allowed.has((c.clinic || "").toUpperCase()));
    }
    return results;
  }, [clinicSummaryData, user]);
  const totalClinics = clinics.length;

  const { reportSubmissions, collectionSubmissions, forecastSubmissions } = useMemo(
    () => overviewSummaryStats(clinicSummaryData),
    [clinicSummaryData],
  );

  // Clinic options for the Forecasting filter — only the user's assigned clinics.
  const forecastingClinicOptions = useMemo(() => {
    const clinicList = user?.clinic || [];
    const summary = clinicSummaryData?.results ?? [];
    const nameMap = new Map(summary.map((c) => [c.clinic, c.clinic_name || ""]));
    return clinicList
      .map((code) => {
        const name = (nameMap.get(code) || "").trim();
        return { value: code, label: name ? `${name} (${code})` : code };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [user?.clinic, clinicSummaryData?.results]);

  // CHANGED: replaces the old auto-select-all approach (which stored 40 codes and showed "40 selected").
  // Empty selection = "All Clinics" dropdown label; effective list expands to all assigned clinics.
  const effectiveForecastingClinics =
    forecastingClinicFilters.length > 0
      ? forecastingClinicFilters
      : forecastingClinicOptions.map((o) => o.value);

  // CHANGED: now uses effectiveForecastingClinics so a batch fetch fires for all clinics
  // when nothing is explicitly selected, without storing them in the dropdown state.
  const { data: batchForecastsData } = useForecastsBatch(
    effectiveForecastingClinics,
    { date_from: forecastBatchDateFrom, date_to: forecastBatchDateTo },
    { enabled: effectiveForecastingClinics.length > 0 },
  );

  // CHANGED: aggregates over effectiveForecastingClinics instead of forecastingClinicFilters.
  const aggregatedForecastSummary = useMemo(() => {
    const active = effectiveForecastingClinics.map((c) => clinicSummaries[c]).filter(Boolean);
    return {
      mtd: active.reduce((s, x) => s + x.mtd, 0),
      forecastFromToday: active.reduce((s, x) => s + x.forecastFromToday, 0),
      monthEnd: active.reduce((s, x) => s + x.monthEnd, 0),
    };
  }, [clinicSummaries, effectiveForecastingClinics]); // eslint-disable-line react-hooks/exhaustive-deps

  // CHANGED: prunes stale summaries based on effectiveForecastingClinics instead of forecastingClinicFilters.
  useEffect(() => {
    setClinicSummaries((prev) => {
      const activeSet = new Set(effectiveForecastingClinics);
      const hasStale = Object.keys(prev).some((k) => !activeSet.has(k));
      if (!hasStale) return prev;
      const next = {};
      for (const c of effectiveForecastingClinics) {
        if (prev[c]) next[c] = prev[c];
      }
      return next;
    });
  }, [effectiveForecastingClinics]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayTotalForProduction = totalClinics;

  return (
    <Page>
      <div className="mb-4">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {tab === "forecasting" ? (
            // Forecasting tab — Month picker + Clinic selector scoped to this Area Manager's clinics
            <MonthlyClinicSelection
              months={[forecastMonthValue]}
              onMonthsChange={(months) => handleForecastMonthChange(months[0])}
              disableCurrentAndFuture={false}
              clinicOptions={forecastingClinicOptions}
              clinicValue={forecastingClinicFilters}
              onClinicChange={setForecastingClinicFilters}
            />
          ) : (
            <DateStatCard label="Date" value={reportDate} onChange={setReportDate} />
          )}

          {tab !== "forecasting" && (
            <>
              <StatCard
                icon="🏠"
                label="Production Report Submissions"
                value={`${reportSubmissions}/${displayTotalForProduction}`}
              />
              <StatCard
                icon="🐷"
                label="Collection Report Submissions"
                value={`${collectionSubmissions}/${totalClinics}`}
              />
              <StatCard
                icon="📈"
                label="Forecast Report Submissions"
                value={`${forecastSubmissions}/${totalClinics}`}
              />
            </>
          )}
        </div>
      </div>

      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { key: "overview", label: "Overview" },
          { key: "forecasting", label: "Forecasting" },
          { key: "profile", label: "Employee Profile" },
        ]}
      />

      {tab === "overview" && (
        <Overview
          clinics={clinics}
          isLoading={isLoadingSummary}
          page={page}
          pageSize={pageSize}
          totalClinics={totalClinics}
          date={reportDate}
          onPageChange={setPage}
          onPageSizeChange={(n) => {
            setPage(1);
            setPageSize(n);
          }}
        />
      )}

      {/* CHANGED: condition and map both use effectiveForecastingClinics — renders all clinics
           when nothing is selected, or only the chosen clinic(s) when a filter is active. */}
      {tab === "forecasting" &&
        (effectiveForecastingClinics.length > 0 ? (
          <div className="space-y-6 mt-2">
            <ForecastingSummaryBar
              mtd={aggregatedForecastSummary.mtd}
              forecastFromToday={aggregatedForecastSummary.forecastFromToday}
              monthEnd={aggregatedForecastSummary.monthEnd}
              // CHANGED: was forecastingClinicFilters.length
              clinicCount={effectiveForecastingClinics.length}
              isPastMonth={forecastIsPastMonth}
            />
            {effectiveForecastingClinics.map((clinic) => {
              const option = forecastingClinicOptions.find((o) => o.value === clinic);
              return (
                <Forecasting
                  key={clinic}
                  clinicId={clinic}
                  hideClinicSelector
                  hideSummary
                  label={option?.label || clinic}
                  onSummaryChange={(s) => setClinicSummaries((prev) => ({ ...prev, [clinic]: s }))}
                  calYearOverride={forecastCalYear}
                  calMonthOverride={forecastCalMonth}
                  onCalMonthChange={handleForecastCalNav}
                  forecastsDataOverride={batchForecastsData?.[clinic] ?? []}
                />
              );
            })}
          </div>
        ) : (
          <Forecasting
            clinicId={null}
            date={reportDate}
            clinics={clinics}
            onDateChange={setReportDate}
            hideClinicSelector
          />
        ))}

      {tab === "profile" && <ProfileDirectoryTabPanel clinics={clinics} role={role} />}
    </Page>
  );
}
